import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import FormAutocomplete from "shared/components/forms/FormAutocomplete";
import FormSelect from "shared/components/forms/FormSelect";
import { cn } from "shared/lib/utils";
import {
  fetchManagedCities,
  fetchManagedLanguages,
  localizeCity,
  localizeLanguage,
  FALLBACK_CITIES,
  type CcCity,
  type CcLanguage,
} from "features/courier-onboarding/services/lookupContentService";
import {
  VEHICLE_TYPES,
  YEARS_RANGES,
  type VehicleType,
  type YearsOfExperienceRange,
} from "features/courier-onboarding/types";
import type { SupportedLanguage } from "shared/i18n/i18n";

interface Props {
  errors: Record<string, string>;
  lockedCity?: boolean;
  /**
   * Vehicle types offered for the selected city. Defaults to the full list
   * when not provided.
   */
  allowedVehicles?: VehicleType[];
}

const VEHICLE_EMOJI: Record<VehicleType, string> = {
  BICYCLE: "🚲",
  ELECTRIC_BIKE: "🚴",
  ELECTRIC_SCOOTER: "🛴",
  MOTORCYCLE: "🛵",
  CAR: "🚗",
};

const mkFallbackLanguage = (id: number, name: string): CcLanguage => ({
  id,
  langEn: name,
  langAr: name,
  langHe: name,
});

const FALLBACK_LANGUAGES: CcLanguage[] = [
  mkFallbackLanguage(-1, "Hebrew"),
  mkFallbackLanguage(-2, "Arabic"),
  mkFallbackLanguage(-3, "English"),
  mkFallbackLanguage(-4, "Russian"),
];

// Maps the app UI language (chosen in the header) to the canonical English
// spoken-language label, used to preselect a sensible default.
const UI_LANG_TO_SPOKEN: Record<SupportedLanguage, string> = {
  he: "Hebrew",
  ar: "Arabic",
  en: "English",
};

/**
 * Delivery area, spoken languages, vehicle and experience fields of the merged
 * data step. Presentational: writes into the onboarding context and renders the
 * error keys resolved by the host step.
 */
const PersonalDetailsFields: React.FC<Props> = ({
  errors,
  lockedCity = false,
  allowedVehicles,
}) => {
  const { t, i18n } = useTranslation();
  const uiLang: SupportedLanguage =
    i18n.language === "he" || i18n.language === "ar" ? i18n.language : "en";
  const { data, setPersonal, setVehicle, setExperience } =
    useCourierOnboarding();
  const p = data.personalInfo;

  const experienceValue =
    data.experienceInfo.hasDeliveryExperience === null
      ? ""
      : data.experienceInfo.hasDeliveryExperience
        ? "yes"
        : "no";
  const [cities, setCities] = useState<CcCity[]>([]);
  const [languages, setLanguages] = useState<CcLanguage[]>(FALLBACK_LANGUAGES);

  useEffect(() => {
    fetchManagedCities()
      .then((list) => setCities(list.length > 0 ? list : FALLBACK_CITIES))
      .catch(() => {
        setCities(FALLBACK_CITIES);
      });
    fetchManagedLanguages()
      .then((list) => {
        if (list.length > 0) setLanguages(list);
      })
      .catch(() => {
        setLanguages(FALLBACK_LANGUAGES);
      });
  }, []);

  // Preselect the spoken language that matches the app UI language (chosen in
  // the header) the first time we land here with no selection yet. We only do
  // this once so the courier can still clear/replace it freely afterwards.
  const didInitLanguage = useRef(false);
  useEffect(() => {
    if (didInitLanguage.current) return;
    if (p.spokenLanguages.length > 0) {
      didInitLanguage.current = true;
      return;
    }
    if (languages.length === 0) return;
    const target = UI_LANG_TO_SPOKEN[uiLang];
    if (target && languages.some((l) => l.langEn === target)) {
      setPersonal({ spokenLanguages: [target] });
    }
    didInitLanguage.current = true;
  }, [languages, uiLang, p.spokenLanguages.length, setPersonal]);

  // The stored value is always the canonical English label (`langEn`) so the
  // backend / Monday sync keeps matching its English dropdown labels.
  const toggleLanguage = (langEn: string) => {
    const exists = p.spokenLanguages.includes(langEn);
    setPersonal({
      spokenLanguages: exists
        ? p.spokenLanguages.filter((l) => l !== langEn)
        : [...p.spokenLanguages, langEn],
    });
  };

  // Vehicle types offered for the selected city (default = full list when the
  // parent does not constrain them).
  const vehicleOptions: VehicleType[] =
    allowedVehicles && allowedVehicles.length > 0
      ? VEHICLE_TYPES.filter((v) => allowedVehicles.includes(v))
      : VEHICLE_TYPES;

  const toggleVehicle = (vehicle: VehicleType) => {
    const current = data.vehicleInfo.vehicleTypes;
    const exists = current.includes(vehicle);
    setVehicle({
      vehicleTypes: exists
        ? current.filter((v) => v !== vehicle)
        : [...current, vehicle],
    });
  };

  // Drop any previously selected vehicle that is no longer offered for the
  // selected city (e.g. after switching to a city with a narrower list).
  const allowedKey = vehicleOptions.join(",");
  useEffect(() => {
    const current = data.vehicleInfo.vehicleTypes;
    const filtered = current.filter((v) => vehicleOptions.includes(v));
    if (filtered.length !== current.length) {
      setVehicle({ vehicleTypes: filtered });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey]);

  const tError = (key: string | undefined): string | undefined =>
    key ? (t(key) as string) : undefined;

  return (
    <>
      <section className="flex flex-col gap-4 border-t border-gray-200 pt-5">
        <h2 className="text-sm font-medium text-gray-700">
          {t("onboarding.personal.detailsTitle")}
        </h2>
        <FormAutocomplete
          id="field-city"
          fieldKey="city"
          label={t("onboarding.personal.city")}
          placeholder={t("onboarding.personal.selectCity") ?? ""}
          value={p.city}
          onChange={(value) => setPersonal({ city: value })}
          options={cities.map((c) => ({
            value: c.nameEn,
            label: localizeCity(c, uiLang),
          }))}
          error={tError(errors.city)}
          disabled={lockedCity}
          required
        />
      </section>

      <section
        data-field="spokenLanguages"
        className="flex scroll-mt-24 flex-col gap-3 border-t border-gray-200 pt-5"
      >
        <div>
          <h3 className="text-sm font-medium text-gray-700">
            {t("onboarding.personal.spokenLanguages")}
            <span className="text-haat-red-600 ms-1">*</span>
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            {t("onboarding.personal.spokenLanguagesHint")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {languages.map((lang) => {
            const active = p.spokenLanguages.includes(lang.langEn);
            return (
              <button
                key={lang.langEn}
                type="button"
                role="checkbox"
                aria-checked={active}
                onClick={() => toggleLanguage(lang.langEn)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full ps-2 pe-4 py-2 text-sm font-semibold border-2 transition-all",
                  active
                    ? "bg-haat-red-50 text-haat-red-700 border-haat-red-500 shadow-sm"
                    : "bg-white text-gray-700 border-gray-200 hover:border-haat-red-300",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "grid h-5 w-5 place-items-center rounded-md border-2 transition-colors",
                    active
                      ? "bg-haat-red-600 border-haat-red-600 text-white"
                      : "border-gray-300 text-transparent",
                  )}
                >
                  <Check size={13} strokeWidth={3.5} />
                </span>
                {localizeLanguage(lang, uiLang)}
              </button>
            );
          })}
        </div>
        {errors.spokenLanguages ? (
          <span className="text-xs font-medium text-red-600">
            {t(errors.spokenLanguages)}
          </span>
        ) : null}
      </section>

      <section
        data-field="vehicle"
        className="flex scroll-mt-24 flex-col gap-3 border-t border-gray-200 pt-5"
      >
        <div>
          <h3 className="text-sm font-medium text-gray-700">
            {t("onboarding.vehicleExperience.vehicleHeading")}
            <span className="text-haat-red-600 ms-1">*</span>
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            {t("onboarding.vehicleExperience.vehicleMultiHint")}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {vehicleOptions.map((v) => {
            const active = data.vehicleInfo.vehicleTypes.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleVehicle(v)}
                aria-pressed={active}
                className={cn(
                  "relative rounded-2xl border-2 p-5 flex flex-col items-center text-center transition-all",
                  active
                    ? "border-haat-red-600 bg-haat-red-50 shadow-sm"
                    : "border-gray-200 bg-white hover:border-haat-red-400 hover:shadow-md",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-2 end-2 flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors",
                    active
                      ? "border-haat-red-600 bg-haat-red-600 text-white"
                      : "border-gray-300 bg-white text-transparent",
                  )}
                >
                  <Check size={12} strokeWidth={3} />
                </span>
                <div className="text-4xl mb-2" aria-hidden>
                  {VEHICLE_EMOJI[v]}
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {t(`onboarding.vehicle.${v}`)}
                </span>
              </button>
            );
          })}
        </div>
        {errors.vehicle ? (
          <span className="text-xs font-medium text-red-600">
            {t(errors.vehicle)}
          </span>
        ) : null}
      </section>

      <section className="flex flex-col gap-3 border-t border-gray-200 pt-5">
        <FormSelect
          id="field-experience"
          fieldKey="experience"
          label={t("onboarding.experience.priorExperience")}
          placeholder={t("onboarding.experience.selectExperience") ?? ""}
          value={experienceValue}
          onChange={(e) => {
            const v = e.target.value;
            setExperience({
              hasDeliveryExperience:
                v === "yes" ? true : v === "no" ? false : null,
              yearsOfExperienceRange:
                v === "no" ? null : data.experienceInfo.yearsOfExperienceRange,
            });
          }}
          options={[
            { value: "yes", label: t("common.yes") },
            { value: "no", label: t("common.no") },
          ]}
          error={
            errors.experience ? (t(errors.experience) as string) : undefined
          }
          required
        />
        {data.experienceInfo.hasDeliveryExperience ? (
          <FormSelect
            id="field-years"
            fieldKey="years"
            label={t("onboarding.experience.yearsLabel")}
            placeholder={t("onboarding.experience.yearsPlaceholder") ?? ""}
            value={data.experienceInfo.yearsOfExperienceRange ?? ""}
            onChange={(e) =>
              setExperience({
                yearsOfExperienceRange: (e.target.value ||
                  null) as YearsOfExperienceRange | null,
              })
            }
            options={YEARS_RANGES.map((r) => ({
              value: r,
              label: t(`onboarding.experience.${r}`),
            }))}
            error={errors.years ? (t(errors.years) as string) : undefined}
            required
          />
        ) : null}
      </section>
    </>
  );
};

export default PersonalDetailsFields;
